import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../../../contexts/DataContext';
import './DraftEventModal.css';

const EVENT_TYPES = [
  { value: 'class_session', label: 'Class Session' },
  { value: 'exam', label: 'Exam / Test' },
  { value: 'lab', label: 'Lab' },
  { value: 'office_hours', label: 'Office Hours' },
  { value: 'review_session', label: 'Review Session' },
  { value: 'holiday', label: 'Holiday / No Class' },
  { value: 'other', label: 'Other' },
];

const ASSIGNMENT_TYPES = [
  { value: 'assignment', label: 'Assignment' },
  { value: 'reading', label: 'Reading' },
  { value: 'exam', label: 'Exam / Test' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'project', label: 'Project' },
  { value: 'checkpoint', label: 'Checkpoint' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

/**
 * DraftEventModal
 * Modal for adding / editing a draft event or assignment during course creation.
 * Draft items are stored only in wizard state until the user finalizes "Create Course".
 */
export default function DraftEventModal({ isOpen, type, id, onClose, defaultDate, onSaved }) {
  const { createCourseDraft, updateDraftEvent, deleteDraftEvent, updateDraftAssignment, deleteDraftAssignment, addDraftEvent, addDraftAssignment } = useData();
  const item = id ? (type === 'assignment'
    ? (createCourseDraft?.draftAssignments || []).find(a => a.id === id)
    : (createCourseDraft?.draftEvents || []).find(e => e.id === id)) : null;

  const defaultDateLocal = defaultDate || (new Date()).toISOString().slice(0,10);
  const defaultStart = '09:00';
  const defaultEnd = '10:00';

  const [form, setForm] = useState({
    title: '',
    date: defaultDateLocal,
    startTime: defaultStart,
    endTime: defaultEnd,
    location: '',
    description: '',
    // Enhanced fields
    eventType: 'class_session',
    assignmentType: 'assignment',
    points: '',
    priority: 'MEDIUM',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      if (type === 'assignment') {
        const dateFromDue = item.dueDate || (item.dueDatetime ? String(item.dueDatetime).split('T')[0] : null) || (item.dueDateObj ? (new Date(item.dueDateObj)).toISOString().slice(0,10) : null) || defaultDateLocal;
        setForm({
          title: item.title || item.name || '',
          date: dateFromDue,
          startTime: '',
          endTime: '',
          location: item.location || '',
          description: item.description || '',
          eventType: 'class_session',
          assignmentType: item.assignmentType || 'assignment',
          points: item.points != null ? String(item.points) : '',
          priority: item.priority || 'MEDIUM',
        });
      } else {
        setForm({
          title: item.title || item.name || '',
          date: (item.date || item.start || item.startDatetime || defaultDateLocal).split ? String(item.date || item.start || item.startDatetime || defaultDateLocal).split('T')[0] : defaultDateLocal,
          startTime: item.startTime || '',
          endTime: item.endTime || '',
          location: item.location || '',
          description: item.description || '',
          eventType: item.eventType || 'class_session',
          assignmentType: 'assignment',
          points: '',
          priority: 'MEDIUM',
        });
      }
    } else {
      setForm({
        title: '', date: defaultDateLocal, startTime: defaultStart, endTime: defaultEnd,
        location: '', description: '',
        eventType: 'class_session', assignmentType: 'assignment', points: '', priority: 'MEDIUM',
      });
    }
  }, [isOpen, id, type, item, defaultDateLocal]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }, []);

  const handleSave = useCallback(async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!form.title || !form.date) return;
    if (type === 'assignment') {
      const patch = {
        title: form.title.trim(),
        description: form.description.trim(),
        dueDate: form.date,
        assignmentType: form.assignmentType,
        points: form.points ? Number(form.points) : null,
        priority: form.priority,
      };
      if (id) {
        updateDraftAssignment(id, patch);
        onSaved && onSaved({ id, ...patch });
      } else {
        const created = await addDraftAssignment(patch);
        onSaved && onSaved(created);
      }
    } else {
      const patch = {
        title: form.title.trim(),
        start: `${form.date}T${form.startTime || '00:00'}:00`,
        end: `${form.date}T${form.endTime || form.startTime || '00:00'}:00`,
        location: form.location.trim(),
        description: form.description.trim(),
        eventType: form.eventType,
      };
      if (id) {
        updateDraftEvent(id, patch);
        onSaved && onSaved({ id, ...patch });
      } else {
        const created = await addDraftEvent(patch);
        onSaved && onSaved(created);
      }
    }
    onClose && onClose();
  }, [form, id, type, updateDraftEvent, updateDraftAssignment, addDraftEvent, addDraftAssignment, onSaved, onClose]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    if (type === 'assignment') deleteDraftAssignment(id);
    else deleteDraftEvent(id);
    onClose && onClose();
  }, [id, type, deleteDraftAssignment, deleteDraftEvent, onClose]);

  if (!isOpen) return null;

  return (
    <div className="draft-event-modal" role="dialog" aria-modal="true" aria-label="Edit draft item">
      <div className="dem-backdrop" onClick={onClose} />
      <div className="dem-panel">
        <div className="dem-header">
          <h3>{item ? (type === 'assignment' ? 'Edit Assignment' : 'Edit Event') : (type === 'assignment' ? 'Add Assignment' : 'Add Event')}</h3>
          <button type="button" className="dem-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form className="dem-form" onSubmit={handleSave}>
          <label className="dem-field">Title*
            <input name="title" value={form.title} onChange={handleChange} maxLength={140} required />
          </label>

          {/* Type selector */}
          {type === 'assignment' ? (
            <div className="dem-row">
              <label className="dem-field">Type
                <select name="assignmentType" value={form.assignmentType} onChange={handleChange} className="dem-select">
                  {ASSIGNMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <label className="dem-field">Priority
                <select name="priority" value={form.priority} onChange={handleChange} className="dem-select">
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              <label className="dem-field">Points
                <input type="number" name="points" value={form.points} onChange={handleChange} min="0" max="9999" placeholder="—" />
              </label>
            </div>
          ) : (
            <label className="dem-field dem-field-inline">Event Type
              <select name="eventType" value={form.eventType} onChange={handleChange} className="dem-select">
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
          )}

          <div className="dem-row">
            <label className="dem-field">{type === 'assignment' ? 'Due Date*' : 'Date*'}
              <input type="date" name="date" value={form.date} onChange={handleChange} required />
            </label>
            {type !== 'assignment' && (
              <>
                <label className="dem-field">Start
                  <input type="time" name="startTime" value={form.startTime} onChange={handleChange} />
                </label>
                <label className="dem-field">End
                  <input type="time" name="endTime" value={form.endTime} onChange={handleChange} />
                </label>
              </>
            )}
          </div>
          <label className="dem-field">Location
            <input name="location" value={form.location} onChange={handleChange} />
          </label>
          <label className="dem-field">Description
            <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
          </label>
          <div className="dem-actions">
            <button type="button" className="ui-btn ui-btn--ghost" onClick={onClose}>Cancel</button>
            {item && <button type="button" className="ui-btn ui-btn--danger" onClick={handleDelete}>Delete</button>}
            <button type="submit" className="ui-btn ui-btn--primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
